/**
 * Organization Registration Link Management
 * Handles unique registration links for organizations
 */

/**
 * Generate a unique registration code for an organization
 * @param {string} organizationId - The organization ID
 * @param {string} organizationName - The organization name
 * @returns {string} Unique registration code
 */
export const generateRegistrationCode = (organizationId, organizationName) => {
    // Create a base64 encoded string with organization info and timestamp
    const data = {
        orgId: organizationId,
        orgName: organizationName.replace(/\s+/g, '').toLowerCase(),
        timestamp: Date.now()
    };

    const encoded = btoa(JSON.stringify(data));
    // Take first 12 characters for a shorter, manageable code
    return encoded.substring(0, 12).toUpperCase();
};

/**
 * Generate the full registration URL for an organization
 * @param {string} organizationId - The organization ID
 * @param {string} organizationName - The organization name
 * @param {string} baseUrl - The base URL of the application (optional)
 * @returns {string} Complete registration URL
 */
export const generateRegistrationUrl = (organizationId, organizationName, baseUrl = window.location.origin) => {
    const code = generateRegistrationCode(organizationId, organizationName);
    return `${baseUrl}/page/register?org=${code}&ref=${organizationId}`;
};

/**
 * Extract organization info from URL parameters
 * @returns {Object|null} Organization info or null if not found
 */
export const getOrganizationFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const orgCode = urlParams.get('org') || urlParams.get('code') || urlParams.get('m'); // Added fallbacks
    let orgRef = urlParams.get('ref');

    if (!orgCode) {
        return null;
    }

    try {
        // Decode the organization code to get basic info
        // Handle potential base64 issues or URL encoding
        const cleanCode = decodeURIComponent(orgCode);
        const decoded = JSON.parse(atob(cleanCode));

        // If ref is missing, try to get it from decoded data
        if (!orgRef && decoded.orgId) {
            orgRef = decoded.orgId;
        }

        return {
            organizationId: orgRef,
            registrationCode: orgCode, // Keep original code
            organizationName: decoded.orgName,
            isOrganizationRegistration: true
        };
    } catch (error) {
        console.error('Error decoding organization registration code:', error);
        return null;
    }
};

/**
 * Validate if a registration code is valid for an organization
 * @param {string} code - The registration code
 * @param {string} organizationId - The organization ID to validate against
 * @returns {boolean} True if valid
 */
export const validateRegistrationCode = (code, organizationId) => {
    try {
        const decoded = JSON.parse(atob(code));
        return decoded.orgId === organizationId;
    } catch (error) {
        return false;
    }
};

/**
 * Copy registration link to clipboard
 * @param {string} url - The registration URL to copy
 * @returns {Promise<boolean>} True if successful
 */
export const copyRegistrationLink = async (url) => {
    try {
        await navigator.clipboard.writeText(url);
        return true;
    } catch (error) {
        // Fallback for browsers that don't support clipboard API
        try {
            const textArea = document.createElement('textarea');
            textArea.value = url;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            return successful;
        } catch (fallbackError) {
            console.error('Failed to copy to clipboard:', fallbackError);
            return false;
        }
    }
};

/**
 * Format organization name for display
 * @param {string} name - Organization name
 * @returns {string} Formatted name
 */
export const formatOrganizationName = (name) => {
    return name.split(/(?=[A-Z])/).join(' ').replace(/^\w/, c => c.toUpperCase());
};
